import React from 'react';
import { useLanguage } from './contexts/LanguageContext';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';

const ResolutionTypeCard = ({ icon, title, description, examples }) => (
	<div className='bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition duration-300'>
		<div className='text-3xl mb-3'>{icon}</div>
		<h4 className='text-xl font-bold mb-2'>{title}</h4>
		<p className='text-gray-600 mb-4'>{description}</p>
		<div className='space-y-2'>
			{examples.map((example, idx) => (
				<div key={idx} className='flex items-start'>
					<span className='text-indigo-600 mr-2'>→</span>
					<span className='text-sm pt-1 text-gray-700'>{example}</span>
				</div>
			))}
		</div>
	</div>
);

const FeatureCard = ({ icon, title, description }) => (
	<div className='flex flex-col items-center text-center'>
		<div className='text-5xl mb-4'>{icon}</div>
		<h3 className='font-bold text-lg mb-2'>{title}</h3>
		<p className='text-gray-600 text-sm'>{description}</p>
	</div>
);

const LandingPage = () => {
	const { t } = useLanguage();
	const navigate = useNavigate();
	const { currentUser } = useAuth();

	const handleCTAClick = () => {
		if (currentUser && currentUser.uid) {
			navigate(`/${currentUser.uid}`);
		} else {
			navigate('/login');
		}
	};

	return (
		<div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50 text-gray-900'>
			{/* Hero Section */}
			<section className='max-w-6xl mx-auto px-6 py-20 md:py-28'>
				<div className='grid grid-cols-1 md:grid-cols-2 gap-12 items-center'>
					<div>
						<h1 className='text-5xl md:text-6xl font-bold leading-tight mb-6 text-gradient'>
							{t('landingHeroTitle')}
						</h1>
						<p className='text-xl text-gray-600 mb-8 leading-relaxed'>{t('landingHeroSubtitle')}</p>
						<div className='flex flex-col sm:flex-row gap-4'>
							<button
								onClick={handleCTAClick}
								className='px-8 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition'
							>
								{t('landingGetStarted')}
							</button>
							<button
								onClick={() =>
									document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })
								}
								className='px-8 py-3 border-2 border-indigo-600 text-indigo-600 rounded-lg font-semibold hover:bg-indigo-50 transition'
							>
								{t('landingLearnMore')}
							</button>
						</div>
						<p className='text-sm text-gray-500 mt-6'>{t('landingAndroidComingSoon')}</p>
					</div>
					<div className='relative'>
						<div className='w-full h-96 bg-gradient-to-br from-indigo-200 to-purple-200 rounded-2xl shadow-xl flex items-center justify-center'>
							<div className='text-6xl'>🎯</div>
						</div>
						<div className='absolute -bottom-4 -right-4 w-32 h-32 bg-yellow-200 rounded-full opacity-30'></div>
					</div>
				</div>
			</section>

			{/* Aim Section */}
			<section className='max-w-6xl mx-auto px-6 py-16 bg-white rounded-2xl shadow-lg'>
				<div className='text-center mb-12'>
					<h2 className='text-4xl font-bold mb-4'>{t('landingAimTitle')}</h2>
					<p className='text-lg text-gray-600 max-w-2xl mx-auto'>{t('landingAimDesc')}</p>
				</div>
				<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'>
					<FeatureCard icon='🔢' title={t('landingFeature1Title')} description={t('landingFeature1Desc')} />
					<FeatureCard icon='📊' title={t('landingFeature2Title')} description={t('landingFeature2Desc')} />
					<FeatureCard icon='📧' title={t('landingFeature3Title')} description={t('landingFeature3Desc')} />
					<FeatureCard icon='🔒' title={t('landingFeature4Title')} description={t('landingFeature4Desc')} />
				</div>
			</section>

			{/* Core Missions Section */}
			<section className='max-w-6xl mx-auto px-6 py-16'>
				<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
					<div className='bg-gradient-to-br from-red-50 to-orange-50 rounded-lg p-6 border-l-4 border-red-500'>
						<h3 className='text-xl font-bold text-red-900 mb-2'>{t('landingMission1Title')}</h3>
						<p className='text-sm text-red-800'>{t('landingMission1Desc')}</p>
					</div>
					<div className='bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6 border-l-4 border-blue-500'>
						<h3 className='text-xl font-bold text-blue-900 mb-2'>{t('landingMission2Title')}</h3>
						<p className='text-sm text-blue-800'>{t('landingMission2Desc')}</p>
					</div>
					<div className='bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 border-l-4 border-green-500'>
						<h3 className='text-xl font-bold text-green-900 mb-2'>{t('landingMission3Title')}</h3>
						<p className='text-sm text-green-800'>{t('landingMission3Desc')}</p>
					</div>
					<div className='bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6 border-l-4 border-purple-500'>
						<h3 className='text-xl font-bold text-purple-900 mb-2'>{t('landingMission4Title')}</h3>
						<p className='text-sm text-purple-800'>{t('landingMission4Desc')}</p>
					</div>
				</div>
			</section>

			{/* Resolution Types Section */}
			<section id='how-it-works' className='max-w-6xl mx-auto px-6 py-16'>
				<div className='text-center mb-12'>
					<h2 className='text-4xl font-bold mb-4'>{t('landingResolutionTypesTitle')}</h2>
					<p className='text-lg text-gray-600'>{t('landingResolutionTypesSubtitle')}</p>
				</div>
				<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
					<ResolutionTypeCard
						icon='✅'
						title={t('landingBinaryType')}
						description={t('landingBinaryDesc')}
						examples={[t('landingBinaryExample1'), t('landingBinaryExample2')]}
					/>
					<ResolutionTypeCard
						icon='🔢'
						title={t('landingCountType')}
						description={t('landingCountDesc')}
						examples={[t('landingCountExample1'), t('landingCountExample2')]}
					/>
					<ResolutionTypeCard
						icon='📈'
						title={t('landingMetricType')}
						description={t('landingMetricDesc')}
						examples={[t('landingMetricExample1'), t('landingMetricExample2')]}
					/>
					<ResolutionTypeCard
						icon='🔥'
						title={t('landingStreakType')}
						description={t('landingStreakDesc')}
						examples={[t('landingStreakExample1'), t('landingStreakExample2')]}
					/>
				</div>
			</section>

			{/* Quotes Section */}
			<section className='max-w-4xl mx-auto px-6 py-16'>
				<div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
					<blockquote className='bg-indigo-50 border-l-4 border-indigo-600 p-8 rounded-lg'>
						<p className='text-xl font-semibold text-gray-800 mb-4'>"{t('landingQuote1')}"</p>
						<p className='text-gray-600'>— {t('landingQuote1Author')}</p>
					</blockquote>
					<blockquote className='bg-purple-50 border-l-4 border-purple-600 p-8 rounded-lg'>
						<p className='text-xl font-semibold text-gray-800 mb-4'>"{t('landingQuote2')}"</p>
						<p className='text-gray-600'>— {t('landingQuote2Author')}</p>
					</blockquote>
				</div>
			</section>

			{/* Features Section */}
			<section className='max-w-6xl mx-auto px-6 py-16'>
				<div className='text-center mb-12'>
					<h2 className='text-4xl font-bold mb-4'>{t('landingFeaturesTitle')}</h2>
				</div>
				<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'>
					<FeatureCard
						icon='⚡'
						title={t('landingFeatureLogging')}
						description={t('landingFeatureLoggingDesc')}
					/>
					<FeatureCard
						icon='📝'
						title={t('landingFeatureHistory')}
						description={t('landingFeatureHistoryDesc')}
					/>
					<FeatureCard
						icon='🔔'
						title={t('landingFeatureReminders')}
						description={t('landingFeatureRemindersDesc')}
					/>
					<FeatureCard
						icon='💾'
						title={t('landingFeatureExport')}
						description={t('landingFeatureExportDesc')}
					/>
				</div>
			</section>

			{/* CTA Section */}
			<section className='bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-20'>
				<div className='max-w-3xl mx-auto px-6 text-center'>
					<h2 className='text-4xl font-bold mb-4'>{t('landingCtaTitle')}</h2>
					<p className='text-lg mb-8 opacity-90'>{t('landingCtaDesc')}</p>
					<button
						onClick={handleCTAClick}
						className='px-10 py-4 bg-white text-indigo-600 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-lg'
					>
						{t('landingCtaButton')}
					</button>
				</div>
			</section>

			{/* Footer */}
			<footer className='bg-gray-900 text-gray-300 py-12'>
				<div className='max-w-6xl mx-auto px-6'>
					<div className='grid grid-cols-1 md:grid-cols-2 gap-8 mb-8'>
						<div>
							<h3 className='text-white font-bold text-lg mb-2'>resolvo.me</h3>
							<p className='text-sm'>{t('landingFooterAbout')}</p>
						</div>
						<div className='text-right'>
							<p className='text-sm'>{t('landingFooterContact')}</p>
						</div>
					</div>
					<div className='border-t border-gray-700 pt-8 text-center text-sm'>
						<p>&copy; 2026 {t('appName')}.</p>
					</div>
				</div>
			</footer>

			<style>{`
				.text-gradient {
					background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
					-webkit-background-clip: text;
					-webkit-text-fill-color: transparent;
					background-clip: text;
				}
			`}</style>
		</div>
	);
};

export default LandingPage;
